import { useState } from 'react';
import { TopNav } from '../components/TopNav';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/Button';
import { ChangePasswordModal } from '../modals/ChangePasswordModal';
import { SettingsNav } from '../components/SettingsNav';
import { useAuth } from '../lib/auth';
import { ROLE_INFO } from '../../../src/services/team/roles';

function Toggle({ on }: { on: boolean }) {
  return (
    <div style={{
      width: 36,
      height: 20,
      background: on ? 'var(--ok)' : 'rgba(10,21,40,0.15)',
      position: 'relative',
      transition: 'background .15s',
      cursor: 'pointer',
    }}>
      <div style={{
        position: 'absolute',
        top: 2,
        left: on ? 18 : 2,
        width: 16,
        height: 16,
        background: '#fff',
        transition: 'left .15s',
        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

export function Settings(): JSX.Element {
  const { user, displayName, role } = useAuth();
  const [defaultRange, setDefaultRange] = useState<'3' | '6' | '12'>('3');
  const [density, setDensity] = useState<'Comfortable' | 'Compact'>('Compact');
  const [showPw, setShowPw] = useState(false);

  const isAdmin = role === 'admin';

  const initials = displayName.split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="ari-app">
      <TopNav />
      <div className="page" style={{ overflow: 'auto' }}>
        <PageHeader title="Settings" sub="Your profile, defaults, and (for admins) light system controls." />
        <div className="settings-grid">
          <SettingsNav />
          <div className="settings-content">
            <div className="settings-card">
              <div className="settings-card-hd">
                <div>
                  <div className="ttl">Profile</div>
                  <div className="sub">Your account details. Email and name are managed by Admin.</div>
                </div>
              </div>
              <div className="settings-card-bd">
                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  <div className="avatar-big">{initials}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'var(--font-serif)', fontSize: 22, color: 'var(--navy)', letterSpacing: '-0.012em' }}>{displayName}</div>
                    <div className="d-meta" style={{ marginTop: 2 }}>{user?.email ?? ''}{role ? ` · ${ROLE_INFO[role].label}` : ''}</div>
                  </div>
                  <Button variant="secondary" onClick={() => setShowPw(true)}>Change password</Button>
                </div>
              </div>
            </div>

            <div className="settings-card">
              <div className="settings-card-hd">
                <div>
                  <div className="ttl">Preferences</div>
                  <div className="sub">Saved per-user. Apply to new reports and report-detail tables.</div>
                </div>
              </div>
              <div className="settings-card-bd">
                <div className="settings-row">
                  <div>
                    <div className="lbl">Default date range</div>
                    <div className="sub">Used by "Generate new report" modal.</div>
                  </div>
                  <div className="segmented" style={{ justifySelf: 'start' }}>
                    {(['3', '6', '12'] as const).map((v) => (
                      <button key={v} className={defaultRange === v ? 'on' : ''} onClick={() => setDefaultRange(v)}>{v} mo</button>
                    ))}
                  </div>
                </div>
                <div className="divider" />
                <div className="settings-row">
                  <div>
                    <div className="lbl">Default visible columns</div>
                    <div className="sub">Hide non-billing columns on the report detail table by default.</div>
                  </div>
                  <div>
                    <select className="select" style={{ minWidth: 280 }}>
                      <option>Billing-relevant only (8 columns)</option>
                      <option>All 21 columns</option>
                      <option>Custom…</option>
                    </select>
                  </div>
                </div>
                <div className="settings-row">
                  <div>
                    <div className="lbl">Table density</div>
                    <div className="sub">Compact gets ~50% more rows on screen.</div>
                  </div>
                  <div className="segmented" style={{ justifySelf: 'start' }}>
                    {(['Comfortable', 'Compact'] as const).map((d) => (
                      <button key={d} className={density === d ? 'on' : ''} onClick={() => setDensity(d)}>{d}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {isAdmin && (
              <div className="settings-card">
                <div className="settings-card-hd">
                  <div>
                    <div className="ttl">Weekly schedule <span className="pill" style={{ marginLeft: 8, verticalAlign: 'middle' }}><span className="pdot" />Admin</span></div>
                    <div className="sub">The cron that auto-generates the weekly Finance report.</div>
                  </div>
                </div>
                <div className="settings-card-bd">
                  <div className="settings-row">
                    <div>
                      <div className="lbl">Cron expression</div>
                      <div className="sub">Runs every Monday at 6:00 AM US-Eastern.</div>
                    </div>
                    <div className="d-mono" style={{ background: 'var(--paper-2)', padding: '6px 10px', display: 'inline-block', justifySelf: 'start', border: '1px solid var(--border-1)' }}>
                      0 6 * * 1
                    </div>
                  </div>
                  <div className="settings-row">
                    <div>
                      <div className="lbl">Auto-generate</div>
                      <div className="sub">Toggling off pauses the cron. Manual generations still work.</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifySelf: 'start' }}>
                      <Toggle on />
                      <span className="d-meta">Enabled</span>
                    </div>
                  </div>
                  <div className="settings-row">
                    <div>
                      <div className="lbl">Run now</div>
                      <div className="sub">Triggers an immediate generation outside the cron.</div>
                    </div>
                    <Button variant="secondary" icon="play" style={{ justifySelf: 'start' }}>Run now</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showPw && <ChangePasswordModal onClose={() => setShowPw(false)} />}
    </div>
  );
}
